import { useState, useEffect } from "react";
import {
  X,
  Send,
  CheckCircle2,
  Copy,
  Check,
  Smartphone,
  Info,
  Trash2,
  Plus,
  Minus,
  ShoppingBag,
  ArrowRight,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useInquiry } from "../context/InquiryContext";

export const ProductInquiryModal = () => {
  const {
    items,
    totalCount,
    totalPrice,
    isModalOpen,
    closeInquiry,
    updateQuantity,
    removeItem,
    clearItems,
  } = useInquiry();

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lastSubmittedItems, setLastSubmittedItems] = useState([]);
  const [lastSubmittedTotal, setLastSubmittedTotal] = useState("");
  const [hasPaidMobilePay, setHasPaidMobilePay] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const mobilePayNumber = "9796ZH";
  const mobilePayRecipient = "HJ3DDesign";

  // Lock body scroll and handle ESC key
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeInquiry();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "unset";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen, closeInquiry]);

  // Reset internal states when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setSubmitted(false);
      setLoading(false);
      setErrorMessage("");
      setCopied(false);
    }
  }, [isModalOpen]);

  const handleCopyMobilePay = () => {
    navigator.clipboard.writeText(mobilePayNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      setErrorMessage("Tilføj venligst mindst ét produkt til listen.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Build structured product list summary
    const productSummaryLines = items.map(
      (item, idx) =>
        `${idx + 1}. ${item.quantity}x ${item.name} (${item.price} pr. stk)`
    );

    data.produkter_liste = productSummaryLines.join("\n");
    data.samlet_pris = totalPrice;
    data.antal_varer = String(totalCount);
    data.mobilepay_paid = hasPaidMobilePay ? "Ja" : "Nej";
    data.subject = `Forespørgsel på ${totalCount} stk. produkt(er) (${totalPrice})`;

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setLastSubmittedItems([...items]);
        setLastSubmittedTotal(totalPrice);
        setSubmitted(true);
        clearItems();
      } else {
        setErrorMessage("Der opstod en fejl ved afsendelse. Prøv venligst igen.");
      }
    } catch (err) {
      setErrorMessage("Der opstod en netværksfejl. Prøv venligst igen.");
    } finally {
      setLoading(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={closeInquiry}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs"
          aria-hidden="true"
        />

        {/* Modal Dialog */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 16 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative w-full max-w-2xl bg-white border border-slate-200 shadow-2xl z-10 my-6 overflow-hidden rounded-none flex flex-col max-h-[92vh]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 bg-slate-50/90 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-orange-500 text-white flex items-center justify-center">
                <ShoppingBag size={18} />
              </div>
              <div>
                <span className="text-[11px] uppercase tracking-wider font-bold text-orange-600">
                  Produktforespørgsel
                </span>
                <h2
                  id="modal-title"
                  className="text-lg sm:text-xl font-bold text-slate-900 leading-tight"
                >
                  {items.length > 0
                    ? `Dine valgte produkter (${totalCount} stk.)`
                    : "Forespørgselsliste"}
                </h2>
              </div>
            </div>
            <button
              onClick={closeInquiry}
              type="button"
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors cursor-pointer"
              aria-label="Luk modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body Content */}
          <div className="p-5 sm:p-6 overflow-y-auto space-y-6">
            {submitted ? (
              <div className="py-8 text-center space-y-5">
                <div className="w-16 h-16 bg-green-100 text-green-600 flex items-center justify-center mx-auto rounded-full">
                  <CheckCircle2 size={36} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Tak for din forespørgsel!
                </h3>
                <p className="text-slate-600 text-sm max-w-md mx-auto leading-relaxed">
                  Vi har modtaget din samlede henvendelse.
                  {hasPaidMobilePay && (
                    <strong className="text-slate-900 block mt-1">
                      Vi har noteret din MobilePay-betaling.
                    </strong>
                  )}
                </p>

                {/* Summary of what was sent */}
                {lastSubmittedItems.length > 0 && (
                  <div className="max-w-md mx-auto bg-slate-50 border border-slate-200 p-4 text-left space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Oversigt over henvendelsen:
                    </p>
                    <ul className="divide-y divide-slate-200/60 text-xs">
                      {lastSubmittedItems.map((item, idx) => (
                        <li
                          key={idx}
                          className="py-1.5 flex items-center justify-between text-slate-700"
                        >
                          <span>
                            {item.quantity}x {item.name}
                          </span>
                          <span className="font-mono font-semibold">
                            {item.price}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="pt-2 border-t border-slate-200 flex justify-between items-center text-sm font-bold text-slate-900">
                      <span>Samlet pris:</span>
                      <span className="text-orange-600 font-mono">
                        {lastSubmittedTotal}
                      </span>
                    </div>
                  </div>
                )}

                <div className="pt-3">
                  <button
                    onClick={closeInquiry}
                    type="button"
                    className="px-6 py-2.5 bg-slate-900 hover:bg-orange-500 text-white font-semibold text-sm transition-colors cursor-pointer inline-flex items-center gap-2"
                  >
                    Luk vindue
                  </button>
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="py-12 text-center space-y-4">
                <div className="w-14 h-14 bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                  <ShoppingBag size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  Listen er tom
                </h3>
                <p className="text-slate-500 text-sm max-w-sm mx-auto">
                  Du har endnu ikke tilføjet nogen produkter til din forespørgsel.
                </p>
                <button
                  type="button"
                  onClick={closeInquiry}
                  className="mt-2 px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold transition-colors cursor-pointer"
                >
                  Gennemse produkter
                </button>
              </div>
            ) : (
              <>
                {/* Product List */}
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Valgte produkter ({items.length})
                    </span>
                    <button
                      type="button"
                      onClick={clearItems}
                      className="text-xs text-red-500 hover:text-red-700 underline font-medium cursor-pointer"
                    >
                      Ryd alle
                    </button>
                  </div>

                  <div className="divide-y divide-slate-100 border border-slate-200 bg-white max-h-60 overflow-y-auto">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 sm:p-3.5 flex items-center gap-3 hover:bg-slate-50/60 transition-colors"
                      >
                        {/* Thumbnail */}
                        <div className="w-14 h-14 bg-slate-50 border border-slate-200 shrink-0 overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover object-center"
                          />
                        </div>

                        {/* Title and price */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-slate-900 truncate">
                            {item.name}
                          </h4>
                          <p className="text-xs text-orange-600 font-mono font-semibold mt-0.5">
                            {item.price}
                          </p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center border border-slate-200 bg-white">
                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(item.id, (item.quantity || 1) - 1)
                            }
                            className="w-7 h-7 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                            aria-label="Formindsk antal"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="w-8 text-center text-xs font-bold font-mono text-slate-900 select-none">
                            {item.quantity || 1}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(item.id, (item.quantity || 1) + 1)
                            }
                            className="w-7 h-7 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                            aria-label="Forøg antal"
                          >
                            <Plus size={12} />
                          </button>
                        </div>

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                          title="Fjern fra liste"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add more products prompt & Total */}
                  <div className="p-3.5 bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-2.5">
                    <button
                      type="button"
                      onClick={closeInquiry}
                      className="text-xs font-bold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 cursor-pointer"
                    >
                      <Plus size={14} /> Tilføj flere produkter fra kataloget
                    </button>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                        Samlet pris:
                      </span>
                      <span className="text-base sm:text-lg font-bold font-mono text-slate-900">
                        {totalPrice}
                      </span>
                    </div>
                  </div>
                </div>

                {/* MobilePay Info Box */}
                <div className="p-4 bg-blue-50/70 border border-blue-100 rounded-none space-y-2.5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-blue-950 font-semibold text-sm">
                      <Smartphone size={18} className="text-blue-600" />
                      <span>Betaling med MobilePay</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyMobilePay}
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 bg-white border border-blue-200 text-blue-800 hover:bg-blue-50 transition-colors cursor-pointer"
                    >
                      {copied ? (
                        <>
                          <Check size={12} className="text-green-600" /> Kopieret!
                        </>
                      ) : (
                        <>
                          <Copy size={12} /> Kopier nr.
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-baseline items-start gap-1 sm:gap-3 pt-1">
                    <span className="text-xs font-medium text-slate-600">
                      MobilePay nummer:
                    </span>
                    <span className="text-lg font-mono font-bold tracking-wider text-slate-900 bg-white px-2 py-0.5 border border-blue-200">
                      {mobilePayNumber}
                    </span>
                    <span className="text-xs text-slate-500 font-medium">
                      ({mobilePayRecipient})
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed flex items-start gap-1.5 pt-0.5">
                    <Info size={14} className="shrink-0 text-blue-500 mt-0.5" />
                    <span>
                      Du kan overføre det samlede beløb (
                      <strong className="text-slate-900">{totalPrice}</strong>)
                      direkte via MobilePay, eller sende forespørgslen først og
                      betale efter aftale.
                    </span>
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot per rule 11 */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <input
                      type="text"
                      name="website_url"
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Dit Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        required
                        name="name"
                        type="text"
                        placeholder="F.eks. Mette Hansen"
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        required
                        name="email"
                        type="email"
                        placeholder="din@email.dk"
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Telefonnummer
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="+45 XX XX XX XX"
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all"
                    />
                  </div>

                  {/* MobilePay Checkbox Box */}
                  <div className="p-3.5 bg-slate-50 border border-slate-200 transition-colors hover:border-slate-300">
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        name="mobilepay_paid_checkbox"
                        checked={hasPaidMobilePay}
                        onChange={(e) => setHasPaidMobilePay(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded-none border-slate-300 text-orange-600 focus:ring-orange-500 cursor-pointer accent-orange-600"
                      />
                      <div className="text-xs">
                        <span className="font-semibold text-slate-900 block">
                          Jeg har overført / betalt med MobilePay ({mobilePayNumber})
                        </span>
                        <span className="text-slate-500 block mt-0.5">
                          Sæt kryds her hvis du har overført det samlede beløb (
                          {totalPrice}) til vores MobilePay.
                        </span>
                      </div>
                    </label>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Besked / Eventuelle ønsker
                    </label>
                    <textarea
                      name="message"
                      rows="3"
                      placeholder="Skriv hvis du har specifikke ønsker til farver, størrelser, afhentning eller forsendelse..."
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all resize-none"
                    ></textarea>
                  </div>

                  {errorMessage && (
                    <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 p-2.5">
                      {errorMessage}
                    </p>
                  )}

                  <div className="pt-2 flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={closeInquiry}
                      className="w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors text-center cursor-pointer"
                    >
                      Fortsæt med at kigge
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto px-6 py-2.5 bg-slate-900 hover:bg-orange-500 text-white text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                    >
                      {loading ? (
                        "Sender..."
                      ) : (
                        <>
                          Send samlet forespørgsel ({totalCount} stk.){" "}
                          <Send size={14} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
